import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  UserPlus,
  Shield,
  Settings,
  Mail,
  Link2,
  Copy,
  MoreVertical,
  Trash2,
  Edit,
  Crown,
  Check,
  X,
  Loader2,
  Clock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lista permessi con etichette
// Lista completa dei permessi - verranno filtrati in base al ruolo
const ALL_PERMISSIONS = [
  { key: 'view_orders', label: 'Visualizza ordini', category: 'Ordini', roles: ['buyer', 'seller'] },
  { key: 'create_orders', label: 'Crea ordini', category: 'Ordini', roles: ['buyer', 'seller'] },
  { key: 'manage_orders', label: 'Gestisci ordini', category: 'Ordini', roles: ['buyer', 'seller'] },
  { key: 'view_catalog', label: 'Visualizza catalogo', category: 'Catalogo', roles: ['seller'] },
  { key: 'manage_catalog', label: 'Gestisci catalogo', category: 'Catalogo', roles: ['seller'] },
  { key: 'view_pricelists', label: 'Visualizza listini', category: 'Listini', roles: ['seller'] },
  { key: 'manage_pricelists', label: 'Gestisci listini', category: 'Listini', roles: ['seller'] },
  { key: 'view_relationships', label: 'Visualizza relazioni', category: 'Relazioni', roles: ['buyer', 'seller'] },
  { key: 'manage_relationships', label: 'Gestisci relazioni', category: 'Relazioni', roles: ['buyer', 'seller'] },
  { key: 'view_chat', label: 'Visualizza chat', category: 'Comunicazioni', roles: ['buyer', 'seller'] },
  { key: 'send_messages', label: 'Invia messaggi', category: 'Comunicazioni', roles: ['buyer', 'seller'] },
  { key: 'manage_users', label: 'Gestisci team', category: 'Amministrazione', roles: ['buyer', 'seller'] },
  { key: 'manage_company', label: 'Gestisci azienda', category: 'Amministrazione', roles: ['buyer', 'seller'] },
];

const TeamPage = () => {
  const { user, isBuyer } = useAuth();
  const { t } = useI18n();
  
  // Filtra i permessi in base al ruolo
  const PERMISSION_LIST = ALL_PERMISSIONS.filter(p => 
    p.roles.includes(isBuyer ? 'buyer' : 'seller')
  );
  
  // Stati per dati
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modalità Groupage
  const [isGroupageMode, setIsGroupageMode] = useState(false);
  const [associationMembers, setAssociationMembers] = useState<any[]>([]);
  
  // Stati per dialog
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  
  // Form ruolo
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    is_default: false,
    permissions: {}
  });
  
  // Form invito
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role_id: '',
    expires_days: 7
  });
  
  const [saving, setSaving] = useState(false);

  // Carica dati iniziali
  useEffect(() => {
    fetchData();
  }, []);

  const checkGroupageMode = async () => {
    try {
      const res = await axios.get(`${API}/associations/my-association/menu`);
      if (res.data.is_association_user && res.data.menu?.team?.custom_mode === 'groupage') {
        setIsGroupageMode(true);
        // Carica membri associazione
        fetchAssociationMembers();
      }
    } catch {
      // Non è utente associazione
    }
  };

  const fetchAssociationMembers = async () => {
    try {
      const meRes = await axios.get(`${API}/auth/me`);
      if (meRes.data.association_id) {
        const membersRes = await axios.get(`${API}/associations/${meRes.data.association_id}/members?limit=100`);
        setAssociationMembers(membersRes.data.members || []);
      }
    } catch (error) {
      console.error('Error fetching association members:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Prima verifica se è in modalità groupage (associazione)
      const menuCheck = await axios.get(`${API}/associations/my-association/menu`).catch(() => null);
      
      if (menuCheck?.data?.is_association_user && menuCheck?.data?.menu?.team?.custom_mode === 'groupage') {
        // È un utente associazione, carica membri associazione
        setIsGroupageMode(true);
        await fetchAssociationMembers();
        setLoading(false);
        return;
      }
      
      // Altrimenti carica dati company standard
      const [usersRes, rolesRes, invitesRes, permsRes] = await Promise.all([
        axios.get(`${API}/company/users`),
        axios.get(`${API}/company/roles`),
        axios.get(`${API}/company/invites`).catch(() => ({ data: [] })),
        axios.get(`${API}/company/my-permissions`)
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setInvites(invitesRes.data);
      setMyPermissions(permsRes.data);
    } catch (error) {
      // Se fallisce perché non ha una company, non mostrare errore
      console.error('TeamPage fetchData error:', error);
      // Verifica se potrebbe essere in modalità groupage
      try {
        const menuCheck = await axios.get(`${API}/associations/my-association/menu`);
        if (menuCheck.data.is_association_user) {
          setIsGroupageMode(true);
          await fetchAssociationMembers();
        }
      } catch {
        toast.error('Errore nel caricamento dati');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verifica permesso manage_users
  const canManageUsers = myPermissions?.is_company_owner || myPermissions?.permissions?.manage_users;

  // === RUOLI ===
  
  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || '',
        is_default: role.is_default || false,
        permissions: role.permissions || {}
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        name: '',
        description: '',
        is_default: false,
        permissions: {
          view_orders: true,
          create_orders: true,
          view_catalog: true,
          view_pricelists: true,
          view_relationships: true,
          view_chat: true,
          send_messages: true
        }
      });
    }
    setShowRoleDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('Inserisci un nome per il ruolo');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: roleForm.name,
        description: roleForm.description,
        is_default: roleForm.is_default,
        permissions: roleForm.permissions
      };
      
      if (editingRole) {
        await axios.put(`${API}/company/roles/${editingRole.id}`, payload);
        toast.success('Ruolo aggiornato');
      } else {
        await axios.post(`${API}/company/roles`, payload);
        toast.success('Ruolo creato');
      }
      
      setShowRoleDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    setDeletingItem({ type: 'role', item: role });
    setShowDeleteAlert(true);
  };

  // === INVITI ===
  
  const openInviteDialog = () => {
    setInviteForm({
      email: '',
      role_id: roles.find(r => r.is_default)?.id || '',
      expires_days: 7
    });
    setShowInviteDialog(true);
  };

  const handleCreateInvite = async () => {
    setSaving(true);
    try {
      const payload = {
        email: inviteForm.email || null,
        role_id: inviteForm.role_id || null,
        expires_days: inviteForm.expires_days
      };
      
      const response = await axios.post(`${API}/company/invites`, payload);
      
      toast.success('Invito creato');
      setShowInviteDialog(false);
      fetchData();
      
      // Mostra il link/codice
      const inviteCode = response.data.invite.code;
      const inviteUrl = `${window.location.origin}/join/${inviteCode}`;
      
      // Copia negli appunti
      navigator.clipboard.writeText(inviteUrl);
      toast.info(`Link invito copiato: ${inviteUrl}`);
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione invito'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeInvite = async (invite) => {
    setDeletingItem({ type: 'invite', item: invite });
    setShowDeleteAlert(true);
  };

  const copyInviteLink = (code) => {
    const url = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiato negli appunti');
  };

  // === UTENTI ===
  
  const handleChangeUserRole = async (userId, roleId) => {
    try {
      await axios.put(`${API}/company/users/${userId}`, { role_id: roleId });
      toast.success('Ruolo utente aggiornato');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'aggiornamento"));
    }
  };

  const handleRemoveUser = async (userItem) => {
    setDeletingItem({ type: 'user', item: userItem });
    setShowDeleteAlert(true);
  };

  // === CONFERMA ELIMINAZIONE ===
  
  const confirmDelete = async () => {
    if (!deletingItem) return;
    
    try {
      if (deletingItem.type === 'role') {
        await axios.delete(`${API}/company/roles/${deletingItem.item.id}`);
        toast.success('Ruolo eliminato');
      } else if (deletingItem.type === 'invite') {
        await axios.delete(`${API}/company/invites/${deletingItem.item.id}`);
        toast.success('Invito revocato');
      } else if (deletingItem.type === 'user') {
        await axios.delete(`${API}/company/users/${deletingItem.item.id}`);
        toast.success('Utente rimosso');
      }
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'eliminazione"));
    } finally {
      setShowDeleteAlert(false);
      setDeletingItem(null);
    }
  };

  // Raggruppa permessi per categoria
  const permissionsByCategory = PERMISSION_LIST.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">
            {isGroupageMode ? 'Team Associazione' : 'Gestione Team'}
          </h1>
          <p className="text-muted-foreground">
            {isGroupageMode 
              ? 'Membri della tua associazione e relativo stato'
              : 'Gestisci utenti, ruoli e permessi della tua azienda'
            }
          </p>
        </div>
        {canManageUsers && !isGroupageMode && (
          <Button onClick={openInviteDialog} data-testid="invite-user-btn">
            <UserPlus className="mr-2 h-4 w-4" />
            Invita Utente
          </Button>
        )}
        {isGroupageMode && user?.is_association_admin && (
          <Button onClick={() => window.location.href = '/association/members'} data-testid="manage-members-btn">
            <Users className="mr-2 h-4 w-4" />
            Gestisci Membri
          </Button>
        )}
      </div>

      {/* Sezione Groupage - Membri Associazione */}
      {isGroupageMode && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Membri dell'Associazione
            </CardTitle>
            <CardDescription>
              {associationMembers.length} membri attivi nella tua associazione
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {associationMembers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nessun membro trovato
                </p>
              ) : (
                associationMembers.map((member: any) => (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{member.user?.name || member.company?.name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">
                          {member.company?.name || 'Azienda non specificata'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={member.is_active ? 'default' : 'secondary'}>
                        {member.is_active ? 'Attivo' : 'Inattivo'}
                      </Badge>
                      {member.member_code && (
                        <span className="text-xs text-muted-foreground font-mono">
                          #{member.member_code}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs Team Azienda (solo se non in modalità groupage) */}
      {!isGroupageMode && (
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Utenti ({users.length})
          </TabsTrigger>
          <TabsTrigger value="roles" className="gap-2">
            <Shield className="h-4 w-4" />
            Ruoli ({roles.length})
          </TabsTrigger>
          {canManageUsers && (
            <TabsTrigger value="invites" className="gap-2">
              <Mail className="h-4 w-4" />
              Inviti ({invites.filter(i => i.is_active).length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab Utenti */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Membri del Team</CardTitle>
              <CardDescription>
                Tutti gli utenti con accesso all'azienda
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map((member) => (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-4 rounded-lg border"
                    data-testid={`team-member-${member.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {member.is_company_owner ? (
                          <Crown className="h-5 w-5 text-amber-500" />
                        ) : (
                          <Users className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {member.name}
                          {member.is_company_owner && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Proprietario
                            </Badge>
                          )}
                          {member.id === user?.id && (
                            <Badge variant="secondary">Tu</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{member.email}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Ruolo */}
                      {canManageUsers && !member.is_company_owner ? (
                        <Select
                          value={member.company_role_id || ''}
                          onValueChange={(value) => handleChangeUserRole(member.id, value)}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Seleziona ruolo" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.filter(r => !r.is_owner_role).map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline">
                          {member.role_info?.name || 'Proprietario'}
                        </Badge>
                      )}
                      
                      {/* Azioni */}
                      {canManageUsers && !member.is_company_owner && member.id !== user?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleRemoveUser(member)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Rimuovi dal team
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
                
                {users.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun membro nel team</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ruoli */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Ruoli e Permessi</CardTitle>
                <CardDescription>
                  Definisci i ruoli e i permessi per il tuo team
                </CardDescription>
              </div>
              {canManageUsers && (
                <Button onClick={() => openRoleDialog()} data-testid="create-role-btn">
                  <Shield className="mr-2 h-4 w-4" />
                  Nuovo Ruolo
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.map((role) => (
                  <div 
                    key={role.id}
                    className="p-4 rounded-lg border"
                    data-testid={`role-${role.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          role.is_owner_role ? 'bg-amber-100 text-amber-600' : 'bg-primary/10 text-primary'
                        }`}>
                          {role.is_owner_role ? (
                            <Crown className="h-4 w-4" />
                          ) : (
                            <Shield className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {role.name}
                            {role.is_default && (
                              <Badge variant="secondary" className="text-xs">Default</Badge>
                            )}
                          </div>
                          {role.description && (
                            <div className="text-sm text-muted-foreground">{role.description}</div>
                          )}
                        </div>
                      </div>
                      
                      {canManageUsers && !role.is_owner_role && (
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openRoleDialog(role)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteRole(role)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Preview permessi */}
                    <div className="flex flex-wrap gap-2">
                      {PERMISSION_LIST.filter(p => role.permissions?.[p.key]).map((perm) => (
                        <Badge key={perm.key} variant="outline" className="text-xs">
                          <Check className="h-3 w-3 mr-1 text-green-500" />
                          {perm.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Inviti */}
        {canManageUsers && (
          <TabsContent value="invites" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Inviti Attivi</CardTitle>
                <CardDescription>
                  Link e codici per invitare nuovi membri
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {invites.filter(i => i.is_active).map((invite) => {
                    const isExpired = new Date(invite.expires_at) < new Date();
                    const role = roles.find(r => r.id === invite.role_id);
                    
                    return (
                      <div 
                        key={invite.id}
                        className={`p-4 rounded-lg border ${isExpired ? 'opacity-50' : ''}`}
                        data-testid={`invite-${invite.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              invite.email ? 'bg-blue-100 text-blue-600' : 'bg-primary/10 text-primary'
                            }`}>
                              {invite.email ? (
                                <Mail className="h-5 w-5" />
                              ) : (
                                <Link2 className="h-5 w-5" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium">
                                {invite.email || 'Link generico'}
                              </div>
                              <div className="text-sm text-muted-foreground flex items-center gap-2">
                                <span>Codice: <code className="bg-muted px-1 rounded">{invite.code}</code></span>
                                {role && <Badge variant="outline" className="text-xs">{role.name}</Badge>}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {isExpired ? (
                                <span className="text-destructive">Scaduto</span>
                              ) : (
                                <span>Scade: {new Date(invite.expires_at).toLocaleDateString('it-IT')}</span>
                              )}
                            </div>
                            
                            {!isExpired && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyInviteLink(invite.code)}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                            
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRevokeInvite(invite)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {invites.filter(i => i.is_active).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nessun invito attivo</p>
                      <Button variant="outline" className="mt-4" onClick={openInviteDialog}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Crea Invito
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
      )}

      {/* Dialog Crea/Modifica Ruolo */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent scrollable className="max-w-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>
              {editingRole ? 'Modifica Ruolo' : 'Nuovo Ruolo'}
            </DialogTitle>
            <DialogDescription>
              Definisci nome, descrizione e permessi del ruolo
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-6">
            {/* Info base */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Ruolo *</Label>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  placeholder="Es: Operatore Vendite"
                  data-testid="role-name-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <div className="font-medium">Ruolo Default</div>
                  <div className="text-sm text-muted-foreground">Assegnato ai nuovi utenti</div>
                </div>
                <Switch
                  checked={roleForm.is_default}
                  onCheckedChange={(checked) => setRoleForm({ ...roleForm, is_default: checked })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Breve descrizione del ruolo..."
                rows={2}
              />
            </div>
            
            <Separator />
            
            {/* Permessi */}
            <div className="space-y-4">
              <h4 className="font-medium">Permessi</h4>
              
              {Object.entries(permissionsByCategory).map(([category, perms]) => (
                <div key={category} className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">{category}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(perms as { key: string; label: string }[]).map((perm) => (
                      <div 
                        key={perm.key}
                        className="flex items-center justify-between p-2 rounded border"
                      >
                        <span className="text-sm">{perm.label}</span>
                        <Switch
                          checked={roleForm.permissions[perm.key] || false}
                          onCheckedChange={(checked) => setRoleForm({
                            ...roleForm,
                            permissions: { ...roleForm.permissions, [perm.key]: checked }
                          })}
                          data-testid={`perm-${perm.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="shrink-0 px-6 py-4 border-t bg-background">
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveRole} disabled={saving} data-testid="save-role-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingRole ? 'Salva Modifiche' : 'Crea Ruolo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Crea Invito */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invita Nuovo Membro</DialogTitle>
            <DialogDescription>
              Crea un invito per aggiungere un utente al team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email (opzionale)</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="nome@azienda.it"
                data-testid="invite-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Se specificata, l'invito sarà riservato a questa email
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select
                value={inviteForm.role_id}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role_id: value })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => !r.is_owner_role).map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                      {role.is_default && ' (default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Validità (giorni)</Label>
              <Select
                value={String(inviteForm.expires_days)}
                onValueChange={(value) => setInviteForm({ ...inviteForm, expires_days: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 giorno</SelectItem>
                  <SelectItem value="7">7 giorni</SelectItem>
                  <SelectItem value="14">14 giorni</SelectItem>
                  <SelectItem value="30">30 giorni</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateInvite} disabled={saving} data-testid="create-invite-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              Crea Invito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Conferma Eliminazione */}
      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Conferma Eliminazione
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem?.type === 'role' && (
                <>Sei sicuro di voler eliminare il ruolo "{deletingItem.item.name}"? Gli utenti con questo ruolo perderanno i permessi associati.</>
              )}
              {deletingItem?.type === 'invite' && (
                <>Sei sicuro di voler revocare questo invito? L'utente non potrà più utilizzarlo per registrarsi.</>
              )}
              {deletingItem?.type === 'user' && (
                <>Sei sicuro di voler rimuovere "{deletingItem.item.name}" dal team? L'utente non avrà più accesso all'azienda.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamPage;
